import * as React from 'react';
import { paths } from 'src/frontend-common/components/Router';
import HomePageBlackLogo from 'src/frontend-common/components/Icons/HomePageBlackLogo';
import { useGetAvailableBudgetQuery } from 'src/redux/services/credits';
import { useDispatch, useSelector } from 'react-redux';
import { toggleForceLoading } from 'src/redux/slices/creditSlice';
import HorizontalMenu from 'src/frontend-common/components/HorizontalMenu';
import { CompanyOption, useGetCompaniesOptions } from 'src/hooks/useGetCompaniesOptions';
import { baseApiWithCompanyId } from 'src/redux/services/baseApi';
import { ItemProps } from 'src/frontend-common/components/HorizontalMenu/CustomAutocomplete';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import {
  AutoAwesomeOutlined,
  CloudDownloadOutlined,
  DashboardOutlined,
  DnsOutlined,
  EmailOutlined,
  SettingsOutlined,
  TravelExploreOutlined,
  HubOutlined,
  PasswordOutlined,
  ScienceOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import KeyIcon from '@mui/icons-material/Key';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import Build from '@mui/icons-material/Build';
import { isAdmin } from 'src/utils/user';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import HorizontalMobileMenu from 'src/frontend-common/components/HorizontalMenu/MobileMenu';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { getActiveCompany, setActiveCompany } from 'src/frontend-common/localstorage';
import { selectUser } from 'src/redux/slices/userSlice';

const Header: React.FC = () => {
  const user = useSelector(selectUser);
  const userName = `${user.first_name} ${user.last_name}`;
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktopUp = useMediaQuery(theme.breakpoints.up('lg'));
  const params = useLocation();
  const dispatch = useDispatch();
  const [activeValue, setActiveValue] = React.useState<CompanyOption | null>(null);
  const { data: availableBudget, isLoading: isLoadingBalance, isFetching } = useGetAvailableBudgetQuery();

  const activeCompany = getActiveCompany();
  const locationState = params.state as any;

  const companies = useGetCompaniesOptions();

  React.useEffect(() => {
    if (activeCompany) {
      setActiveValue({
        key: activeCompany.id,
        title: activeCompany.name,
      });
    }
  }, []);

  const submenus = [
    {
      buttonIcon: <DashboardOutlined />,
      buttonTitle: 'Dashboard',
      link: () => navigate(paths.home.path, { state: { menu: 'Dashboard' } }),
    },
    {
      buttonIcon: <SearchOutlined />,
      buttonTitle: 'Lookups',
      menuItems: [
        {
          icon: <FormatListBulletedOutlinedIcon />,
          link: () => navigate(paths.lookups.path, { state: { menu: 'Lookups' } }),
          title: 'List',
        },
        {
          icon: <CloudUploadOutlinedIcon />,
          link: () => navigate(paths.import.path, { state: { menu: 'Lookups' } }),
          title: 'Uploads',
        },
        {
          icon: <CloudDownloadOutlined />,
          link: () => navigate(paths.export.path, { state: { menu: 'Lookups' } }),
          title: 'Downloads',
        },
      ],
    },
    {
      buttonIcon: <ScienceOutlined />,
      buttonTitle: 'Simulator',
      menuItems: [
        {
          icon: <AdminPanelSettingsOutlinedIcon />,
          link: () => navigate(`${paths.lookup.path}?tab=0`),
          title: 'Onboarding',
        },
        {
          icon: <SmartphoneOutlinedIcon />,
          link: () => navigate(`${paths.lookup.path}?tab=1`),
          title: 'Phone Number',
        },
        {
          icon: <EmailOutlined />,
          link: () => navigate(`${paths.lookup.path}?tab=2`),
          title: 'Email Address',
        },
        {
          icon: <TravelExploreOutlined />,
          link: () => navigate(`${paths.lookup.path}?tab=3`),
          title: 'IP Address',
        },
        {
          icon: <DnsOutlined />,
          link: () => navigate(`${paths.lookup.path}?tab=4`),
          title: 'Domain',
        },
        {
          icon: <PasswordOutlined />,
          link: () => navigate(`${paths.lookup.path}?tab=5`),
          title: 'Login',
        },
      ],
    },
    {
      buttonIcon: <AssessmentOutlinedIcon />,
      buttonTitle: 'Analytics',
      menuItems: [
        {
          icon: <AdminPanelSettingsOutlinedIcon />,
          link: () => navigate(`${paths.analytics.path}?tab=0`),
          title: 'Onboarding',
        },
        {
          icon: <SmartphoneOutlinedIcon />,
          link: () => navigate(`${paths.analytics.path}?tab=1`),
          title: 'Phone Number',
        },
        {
          icon: <EmailOutlined />,
          link: () => navigate(`${paths.analytics.path}?tab=2`),
          title: 'Email Address',
        },
        {
          icon: <TravelExploreOutlined />,
          link: () => navigate(`${paths.analytics.path}?tab=3`),
          title: 'IP Address',
        },
        {
          icon: <DnsOutlined />,
          link: () => navigate(`${paths.analytics.path}?tab=4`),
          title: 'Domain',
        },
        {
          icon: <PasswordOutlined />,
          link: () => navigate(`${paths.analytics.path}?tab=5`),
          title: 'Login',
        },
      ],
    },
    {
      buttonIcon: <TuneOutlinedIcon />,
      buttonTitle: 'Models',
      menuItems: [
        {
          icon: <FormatListBulletedOutlinedIcon />,
          link: () => navigate(paths.ruleEngine.path, { state: { menu: 'Models' } }),
          title: 'List',
        },
        {
          icon: <AutoAwesomeOutlined />,
          link: () => navigate(paths.insights.path, { state: { menu: 'Models' } }),
          title: 'Insights',
        },
      ],
    },
    {
      buttonIcon: <HubOutlined />,
      buttonTitle: 'Graph',
      link: () => navigate(paths.identityGraph.path, { state: { menu: 'Graph' } }),
    },
    {
      buttonIcon: <PermIdentityOutlinedIcon />,
      buttonTitle: 'Admin',
      isAdminOnlyContent: true,
      menuItems: [
        {
          icon: <BusinessOutlinedIcon />,
          link: () => navigate(paths.companies.path, { state: { menu: 'Admin' } }),
          title: 'Companies',
        },
        {
          icon: <PeopleAltOutlinedIcon />,
          link: () => navigate(paths.users.path, { state: { menu: 'Admin' } }),
          title: 'Users',
        },
        {
          icon: <KeyIcon />,
          link: () => navigate(paths.apps.path, { state: { menu: 'Admin' } }),
          title: 'Apps',
        },
        {
          icon: <ReceiptLongOutlinedIcon />,
          link: () => navigate(paths.customPricing.path, { state: { menu: 'Admin' } }),
          title: 'Billing & Plans',
        },
        {
          icon: <Build />,
          link: () => navigate(paths.utilities.path, { state: { menu: 'Admin' } }),
          title: 'Admin Utilities',
        },
        {
          icon: <AssessmentOutlinedIcon />,
          link: () => navigate(paths.adminDashboard.path, { state: { menu: 'Admin' } }),
          title: 'Admin Dashboard',
        },
      ],
    },
  ];

  const menuProps = {
    activeCompany: activeValue?.title || '',
    balance: availableBudget?.['total'],
    companies: companies[0] as CompanyOption[],
    isLoadingBalance: isLoadingBalance,
    returnToHomeFn: () => navigate('/', { state: { menu: 'Dashboard' } }),
    isLoadingCompanies: companies[0]?.length === 0,
    defaultSelectedMenu: {
      main: locationState && locationState.menu ? locationState.menu : 'Dashboard',
    },
    icon: <HomePageBlackLogo height={27} width={110} />,
    setItem: (newValue: ItemProps | null) => {
      let newCompany = undefined;
      if (companies[1]) {
        const noDuplicatedCompanies = companies[1] as {
          id: string;
          name: string;
        }[];
        newCompany = noDuplicatedCompanies.find((c) => c.id === (newValue as any)?.key);
      }

      if (newCompany) {
        setActiveCompany(newCompany);
        window.dispatchEvent(new Event('storage'));
        dispatch(baseApiWithCompanyId.util.resetApiState());
      }
    },
    profileMenu: {
      company: activeValue?.title || '',
      full_name: userName,
      imageSrc: 'https//',
      logoutFunction: () => {
        localStorage.removeItem('accessToken');
        navigate('/login');
      },
      menuItems: [
        {
          buttonIcon: <SettingsOutlined />,
          link: () => navigate(paths.settings.path, { state: { tab: 0 } }),
          buttonTitle: 'Profile',
        },
        {
          buttonIcon: <ReceiptLongOutlinedIcon />,
          link: () => navigate(paths.settings.path, { state: { tab: 2 } }),
          buttonTitle: 'Billing & Plans',
        },
        {
          buttonIcon: <KeyIcon />,
          link: () => navigate(paths.appKeys.path, { state: { tab: 2 } }),
          buttonTitle: 'App Keys',
        },
      ],
    },
    submenus: submenus,
    isUserAdmin: isAdmin(user),
  };

  React.useEffect(() => {
    if (isFetching) {
      dispatch(toggleForceLoading());
    }
  }, [isFetching]);

  return <>{isDesktopUp ? <HorizontalMenu {...menuProps} /> : <HorizontalMobileMenu {...menuProps} />}</>;
};

export default Header;
